import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CommonService } from './common.service';
import {
  BuyerDetails,
  CheckOutReleaseObj,
  Options,
  ProcessOrderRequestModelNew,
  UpdatedCheckout
} from '../models/enum';
import { Observable, of } from 'rxjs';
import { MaskingData } from '../shared/phone-input/phone-input.component';
import {
  IApiResponseModal,
  IGiftCardResponseModal
} from '../models/common-interface';
import { PaymentGatewayInfo } from '../home/switch-ticket/switch-ticket';

export enum ReleaseSeatFromCartIn {
  Timer = 'Timer',
  CloseIcon = 'CloseIcon',
  BackToTickets = 'BackToTickets'
}

@Injectable({
  providedIn: 'root'
})
export class CheckoutService {
  private http = inject(HttpClient);
  private commonService = inject(CommonService);

  CHECKOUT_API_URL = environment.apiBaseAddress + '/CheckOut';
  PAYMENTGATEWAY_API_URL = environment.publicApiBaseAddress + '/PaymentGateway';

  getCheckOutPageDetails(
    orderIdentifier,
    productType,
    isClassPassSeasonPassOrder,
    isCashOrder
  ) {
    const reqParam = {
      productType: productType,
      isClassSeasonPassOrder: isClassPassSeasonPassOrder,
      isCashOrder: isCashOrder
    };
    return this.http.get<any>(
      this.CHECKOUT_API_URL + '/GetCheckOutPageDetails/' + orderIdentifier,
      { params: reqParam }
    );
  }

  purchaseOrder(orderIdentifier, reqModel) {
    const reqParam = {
      orderIdentifier: orderIdentifier
    };
    return this.http.post<any>(
      this.CHECKOUT_API_URL + '/ProcessOrder',
      reqModel,
      { params: reqParam }
    );
  }

  getTOCPageDetails(showId, tocId: number = 0) {
    if (showId || tocId) {
      const reqParam: any = {};
      if (showId) {
        reqParam.showIds = showId;
      }
      if (tocId) {
        reqParam.tocId = tocId;
      }
      return this.http.get<any>(this.CHECKOUT_API_URL + '/GetTOCForEmbed', {
        params: reqParam
      });
    } else {
      return this.http.get<any>(this.CHECKOUT_API_URL + '/GetTOCForEmbed');
    }
  }

  orderNoteCreate(paypalOrderId) {
    const reqParam = {
      orderIdentifier: this.commonService.orderIdentifier,
      paypalOrderId: paypalOrderId
    };
    return this.http.post<any>(
      this.CHECKOUT_API_URL + '/OrderNoteCreate',
      reqParam
    );
  }

  releaseTicketsFromCart(
    releaseObj: CheckOutReleaseObj | string,
    releaseSeatFromCartIn: ReleaseSeatFromCartIn = ReleaseSeatFromCartIn.CloseIcon
  ) {
    const options: Options = <Options>{};
    if (typeof releaseObj !== 'string') {
      options.body =
        releaseObj && releaseObj.additionalOrderItems
          ? releaseObj.additionalOrderItems
          : [];
    } else {
      options.body = [];
    }
    const orderIdentifier: string =
      typeof releaseObj === 'string' ? releaseObj : releaseObj.orderIdentifier;

    let releaseCartURL = '';
    if (releaseSeatFromCartIn === ReleaseSeatFromCartIn.BackToTickets)
      releaseCartURL = 'ReleaseSeatFromBackToTickets';
    else if (releaseSeatFromCartIn === ReleaseSeatFromCartIn.Timer)
      releaseCartURL = 'ReleaseSeatFromCartTimer';
    else releaseCartURL = 'ReleaseSeatFromCart';

    if (!this.commonService.isOrderProcessing) {
      return this.http.delete<any>(
        this.CHECKOUT_API_URL +
          `/${releaseCartURL}?orderIdentifier=` +
          orderIdentifier,
        options
      );
    } else {
      this.commonService.setStartorStopOrderProcessing(false);
      return of(null);
    }
  }

  public releaseTicketsFromCartNative(
    orderIdentifier: string,
    releaseSeatFromCartIn: ReleaseSeatFromCartIn = ReleaseSeatFromCartIn.CloseIcon
  ) {
    if (this.commonService.isOpenSuperbFromAttendeeAPP || this.commonService.isOpenSuperbFromBoxOfficeAPP) {
      return;
    }
    let releaseCartURL = '';
    if (releaseSeatFromCartIn === ReleaseSeatFromCartIn.BackToTickets)
      releaseCartURL = 'ReleaseSeatFromBackToTickets';
    else if (releaseSeatFromCartIn === ReleaseSeatFromCartIn.Timer)
      releaseCartURL = 'ReleaseSeatFromCartTimer';
    else releaseCartURL = 'ReleaseSeatFromCart';

    if (!this.commonService.isOrderProcessing && orderIdentifier) {
      const url = `${this.CHECKOUT_API_URL}/${releaseCartURL}?orderIdentifier=${orderIdentifier}`;
      const token = this.commonService.token ? `Bearer ${this.commonService.token}` : '';
      try {
        fetch(url, {
          method: 'DELETE',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            'application': this.commonService.getApplicationId(),
            'isBoxOffice': (this.commonService.isOpenSuperbFromAttendeeAPP || this.commonService.isOpenSuperbFromBoxOfficeAPP) ? '1' : '0',
            'originType': this.commonService.originType,
            'Authorization': token
          },
          body: JSON.stringify([])
        });
      } catch (e) {
        console.error('Failed to release carts natively', e);
      }
    }
  }

  removeOrderItemsFromCheckout(body) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: body
    };
    const url = this.CHECKOUT_API_URL + '/RemoveOrderItems/';
    return this.http.delete<any>(url, options);
  }

  getOrderSummary(orderIdentifier, isNewAppView: boolean = false, isBestAvailableTicket: boolean = false) {
    let url = this.CHECKOUT_API_URL + '/GetOrderSummary/' + orderIdentifier;
    const queryParams: string[] = [];
    if (isNewAppView) {
      queryParams.push('isNewAppView=true');
    }
    if (isBestAvailableTicket) {
      queryParams.push('isBestAvailableTicket=true');
    }
    if (queryParams.length > 0) {
      url += '?' + queryParams.join('&');
    }
    return this.http.get<any>(url);
  }

  applyRemoveMembershipAfterCheckout(orderIdentifier, promocode = null) {
    const url =
      this.CHECKOUT_API_URL +
      '/ApplyRemoveMembershipAfterCheckout/' +
      orderIdentifier +
      '/' +
      promocode;

    return this.http.post<any>(url, null);
  }

  logPaymentGatewayError(body) {
    const url = this.PAYMENTGATEWAY_API_URL + '/paymentGatewayError';
    return this.http.post<any>(url, body);
  }

  public updateOrderDetails(reqModel: BuyerDetails) {
    return this.http.post<UpdatedCheckout>(
      this.CHECKOUT_API_URL + '/UpdateOrderDetails',
      reqModel
    );
  }

  public purchaseOrderFrmMultiStepsVersion(
    reqModel: ProcessOrderRequestModelNew
  ) {
    return this.http.post<UpdatedCheckout>(
      this.CHECKOUT_API_URL + '/ProcessOrderForNewEmbedCheckOut',
      reqModel
    );
  }

  // TO Post the user details like email and phone number
  public updateInCompleteOrderDetails(body: Object): Observable<any> {
    return this.http.post<Object>(
      this.CHECKOUT_API_URL +
        `/UpdateInCompleteOrderDetails/${this.commonService.orderIdentifier}`,
      body
    );
  }

  public fetchJson(): Observable<MaskingData[]> {
    return this.http.get<MaskingData[]>('../../../assets/json/masking.json');
  }

  public getPaymentGatewayInfo(): Observable<PaymentGatewayInfo> {
    return this.http.get<PaymentGatewayInfo>(
      this.CHECKOUT_API_URL + '/GetCheckOutPagePaymentGatewayInfo'
    );
  }

  public calculateGiftcardDiscount(
    body: any
  ): Observable<IGiftCardResponseModal> {
    const reqParam = {
      orderIdentifier: this.commonService.orderIdentifier
    };
    return this.http.post<IGiftCardResponseModal>(
      this.CHECKOUT_API_URL + `/CalculateGiftcardDisount`,
      body,
      { params: reqParam }
    );
  }

  // For the New checkout and orderSummary UI when partial payment and user navigate back to ticket's need to release gift card;
  public releaseGiftCardOnPartialPayment(
    orderIdentifier: string,
    isRefundAllGiftCard: boolean = false,
    transactionIdentifier: string = ''
  ): Observable<IApiResponseModal<string>> {
    if (transactionIdentifier) {
      return this.http.get<IApiResponseModal<string>>(
        `${
          this.CHECKOUT_API_URL
        }/ProcessGiftCardOnPartialPayment/${orderIdentifier}?transactionIdentifier=${transactionIdentifier}`
      );
    } else if (isRefundAllGiftCard) {
      return this.http.get<IApiResponseModal<string>>(
        `${
          this.CHECKOUT_API_URL
        }/ProcessGiftCardOnPartialPayment/${orderIdentifier}?isRefundAllGiftCard=true`
      );
    } else {
      return this.http.get<IApiResponseModal<string>>(
        `${
          this.CHECKOUT_API_URL
        }/ProcessGiftCardOnPartialPayment/${orderIdentifier}`
      );
    }
  }

  public GetStripPaymentStatusByIntentId(orderIdentifier: string, paymentIntentId: string) {
    return this.http.get<any>(
      `${this.CHECKOUT_API_URL}/GetStripPaymentStatusByIntentId/${orderIdentifier}?paymentIntentId=${paymentIntentId}`
    );
  }

  public generatePaypalOrderId(isExchange: boolean = false, amount?: number, paymentLinkToken?: string): Observable<any> {
    let url = `${this.CHECKOUT_API_URL}/GeneratePaypalOrderId/${this.commonService.orderIdentifier}`;
    if (isExchange) {
      url += '?isExchange=true';
      if (amount !== undefined && amount !== null) {
        url += `&amount=${amount}`;
      }
      if (paymentLinkToken) {
        url += `&paymentLinkToken=${paymentLinkToken}`;
      }
    }
    return this.http.post<any>(url, null);
  }

  public getGiftCardDiscounts(orderIdentifier: string) {
    return this.http.get<any>(
      this.CHECKOUT_API_URL + '/GetGiftCardDiscounts/' + orderIdentifier
    );
  }
}
